/**
 * The door A3 and A7 share: a number, a channel, a code asked for — and, when the person
 * chooses Telegram, the one extra step Telegram needs.
 *
 * A3 (`/auth/phone`, a sign-up) and A7 (`/auth/login`, a return) are drawn differently
 * and ask the Worker the same thing, so the asking lives here once and each screen keeps
 * its own layout. Before W2-12 each screen carried its own copy of the send logic, and
 * they had already drifted by one comment.
 *
 * ── THE TELEGRAM STEP, AND WHY IT IS A STEP ──────────────────────────────────
 *
 * A code can reach Telegram only through a chat the bot knows, and a brand-new person
 * has no chat the bot knows. Door B (`apps/api/src/auth/channels/telegram.ts`) is how one
 * is made: `POST /v1/auth/telegram/start-link` returns a `t.me` deep link, the person
 * presses স্টার্ট in Telegram, and the bot binds that chat to the number for an hour. Only
 * THEN can `/otp/request` with `channel: 'telegram'` deliver anything.
 *
 * Nothing tells the screen that স্টার্ট was pressed — deliberately: an endpoint that
 * answered "is this number linked" would answer "is this number registered" to anyone.
 * So the step is the person's to finish, and the screen says so: choosing Telegram turns
 * the button into টেলিগ্রাম খুলুন, which opens the link in a NEW TAB (this one keeps the
 * number typed), shows what to do there, and turns the button back into কোড পাঠান.
 *
 * Pressing কোড পাঠান before স্টার্ট is not a dead end, and it is not hidden either: the
 * Worker falls back to WhatsApp, and failing that lets a new number in unverified
 * (docs/07 § 4) — the "you're in" frame in `auth/failure.tsx`.
 *
 * ── WHAT A PRESS NEVER DOES ──────────────────────────────────────────────────
 *
 *   · send a number the field has already flagged — a code is one of five an hour;
 *   · send a Turnstile token twice (`turnstile.ts § a token is spent once`);
 *   · open anything but a `t.me` link (`auth/otp.ts § isTelegramDeepLink`).
 */
import { useEffect, useRef, useState, type MouseEvent, type RefObject } from 'react';
import { useNavigate } from 'react-router-dom';
import { Admitted, InlineAnswer, answerFor, isInline, noteFailure, renderAuthFailure, type AuthAnswer } from '../../auth/failure.js';
import {
  fetchChannels,
  isPlausibleMobile,
  isTelegramDeepLink,
  requestOtp,
  startTelegramLink,
  toE164,
  type ChannelOption,
  type OtpChannel,
} from '../../auth/otp.js';
import { ONBOARDING_COPY } from './copy.js';
import { FIELD_ERROR_LINE, Radio } from './frame.js';
import { takeTurnstileToken, useTurnstile } from './turnstile.js';

/* ══════════════════════════════════════════════════════ the hand-over ══ */

/** Which door A4 was reached through. A3 is a sign-up, A7 a return. */
export type CodeIntent = 'signup' | 'login';

/** What A3 and A7 hand A4 in `location.state`. */
export interface CodeScreenState {
  phone_e164: string;
  intent: CodeIntent;
  /** When the code that was just sent stops working — A4's countdown counts to it. */
  expires_at?: string | undefined;
  /** The channel the person ASKED for, so a resend asks for the same one. */
  channel?: OtpChannel | undefined;
  /** A7's `?next=`, already checked by `safeNext`. */
  next?: string | undefined;
}

/**
 * `?next=` as A7 receives it from the sign-in door (`auth/SignInPrompt.tsx`), or `null`.
 *
 * Only a path this app could have produced: one leading `/`, not `//` and not `/\` —
 * both of which a browser reads as another host. An open redirect after a person has just
 * proved who they are is a phishing link with our name on it.
 */
export function safeNext(search: string): string | null {
  const raw = new URLSearchParams(search).get('next');
  if (raw === null || raw === '') return null;
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\')) return null;
  return raw;
}

/* ═════════════════════════════════════════════════════ Telegram, opened ══ */

/**
 * Open the deep link in a new tab, keeping this one.
 *
 * A new tab, not `location.assign`: on a phone the `t.me` link hands over to the Telegram
 * app, and when the person comes back the tab they return to must still be A3 with their
 * number in it — not a `t.me` page. `opener` is cut so the opened page cannot reach back.
 * A blocked pop-up returns `false`; the panel then shows the same link to tap, which a
 * browser never blocks.
 */
export function openTelegramLink(url: string): boolean {
  if (!isTelegramDeepLink(url) || typeof window === 'undefined') return false;
  try {
    const opened = window.open(url, '_blank');
    if (opened === null) return false;
    try {
      opened.opener = null;
    } catch {
      /* a cross-origin window may refuse the write; noopener is then the browser's */
    }
    return true;
  } catch {
    return false;
  }
}

export type TelegramBegin =
  | { outcome: 'opened'; deep_link: string; expires_in_seconds: number }
  | { outcome: 'unavailable' }
  | { outcome: 'throttled'; retry_after_seconds: number };

/**
 * Door B's first half, as a screen presses it: ask the Worker for the link, open it.
 *
 * A function and not a click handler, so the acceptance test for W2-12 — choosing
 * Telegram calls start-link and opens the returned `t.me` URL — can assert the exact
 * request and the exact URL opened without a DOM.
 */
export async function beginTelegram(
  phone_e164: string,
  open: (url: string) => boolean = openTelegramLink,
): Promise<TelegramBegin> {
  const result = await startTelegramLink(phone_e164);
  if (result.outcome !== 'link') return result;
  open(result.deep_link);
  return { outcome: 'opened', deep_link: result.deep_link, expires_in_seconds: result.expires_in_seconds };
}

/**
 * Where the Telegram step stands for the number in the field.
 *
 *   idle     Telegram not opened for this number.
 *   opened   the deep link was opened; the next press asks for the code over Telegram.
 *   direct   an EXISTING identity chose Telegram from the honest wait: door B cannot bind
 *            a chat to a registered number, so there is no link to open — the code goes
 *            to a chat the account linked from inside itself, or the Worker says wait.
 */
export type TelegramStep =
  | { kind: 'idle' }
  | { kind: 'opened'; phone_e164: string; deep_link: string; expires_at_ms: number }
  | { kind: 'direct'; phone_e164: string };

/** The step, for the number in the field now. A step for another number is no step. */
export function stepFor(step: TelegramStep, phone_e164: string | null): TelegramStep {
  if (step.kind === 'idle' || phone_e164 === null) return { kind: 'idle' };
  return step.phone_e164 === phone_e164 ? step : { kind: 'idle' };
}

/**
 * The step a SUCCESSFUL resend leaves behind — kept, not cleared.
 *
 * A code resent over Telegram goes into the chat the deep link already bound, so the link
 * is good for the rest of its fifteen minutes and A4's button must stay কোড পাঠান. Putting
 * টেলিগ্রামে কোড নিন back would make the next press ask `start-link` for a SECOND link to
 * a bot the person has already started — one more of the five first-contact links an hour
 * (`apps/api/src/auth/channels/telegram.ts § MAX_FIRST_CONTACT_TOKENS_PER_HOUR`) — and two
 * of those leave them one press from a 429 on the channel that currently carries every
 * code. A step whose link has expired IS dropped: then the next press genuinely needs a
 * fresh one.
 */
export function stepAfterResend(step: TelegramStep, now: number = Date.now()): TelegramStep {
  return step.kind === 'opened' && now < step.expires_at_ms ? step : { kind: 'idle' };
}

/* ═══════════════════════════════════════════════════════════ the views ══ */

/**
 * The channel choice — A1's row vocabulary (white, `1.5px` teal when chosen, the same
 * 20px radio), at A3's 52px button height rather than A1's 60px, because two of them sit
 * under a field instead of filling a screen. The labels and the hint are the Worker's.
 */
export function ChannelChoice({
  options,
  chosen,
  onChoose,
}: {
  options: readonly ChannelOption[];
  chosen: OtpChannel | null;
  onChoose: (key: OtpChannel) => void;
}): JSX.Element | null {
  if (options.length < 2) return null;
  // A hint belongs to its row, but it is read before a row is chosen — it is the reason to
  // choose it — so every row's hint is shown under the group, not only the chosen one's.
  const hints = options.flatMap((o) => (o.hint_bn === undefined ? [] : [o.hint_bn]));
  return (
    <div>
      <div
        id="door-channel-label"
        data-copy-status={ONBOARDING_COPY.copy_status}
        className="font-anek font-medium text-[14px] leading-[1.75] text-ink2 mb-[7px]"
      >
        {ONBOARDING_COPY.door.channel_label_bn}
      </div>
      <div role="radiogroup" aria-labelledby="door-channel-label" className="flex flex-col gap-[8px]">
        {options.map((option) => {
          const on = option.key === chosen;
          return (
            <button
              key={option.key}
              type="button"
              role="radio"
              aria-checked={on}
              data-channel={option.key}
              onClick={() => onChoose(option.key)}
              className={
                'bg-white h-[52px] flex items-center justify-between px-[15px] text-left rounded-none ' +
                (on ? 'border-[1.5px] border-teal' : 'border border-border')
              }
            >
              <span className="font-anek font-medium text-[15px] leading-[1.75] text-ink">{option.label_bn}</span>
              <Radio selected={on} />
            </button>
          );
        })}
      </div>
      {hints.map((hint) => (
        <div key={hint} className="font-anek font-normal text-[13px] leading-[1.75] text-muted mt-[6px]">
          {hint}
        </div>
      ))}
    </div>
  );
}

/**
 * What to do in Telegram, shown once the link was opened. The link is repeated as a link
 * — a tap on an anchor is never a blocked pop-up — and, past the token's fifteen minutes,
 * asks for a fresh one instead of opening a dead one.
 */
export function TelegramPanel({
  step,
  onReopen,
  className,
}: {
  step: TelegramStep;
  onReopen: () => void;
  className?: string | undefined;
}): JSX.Element | null {
  if (step.kind !== 'opened') return null;
  const copy = ONBOARDING_COPY.telegram;
  const reopen = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (Date.now() < step.expires_at_ms) return;
    event.preventDefault();
    onReopen();
  };
  return (
    <div
      data-telegram-step="opened"
      data-copy-status={ONBOARDING_COPY.copy_status}
      role="status"
      className={'bg-white border border-border px-[15px] py-[13px]' + (className ? ' ' + className : '')}
    >
      <div className="font-anek font-medium text-[15px] leading-[1.75] text-ink">{copy.step_title_bn}</div>
      <div className="font-anek font-normal text-[14px] leading-[1.75] text-body mt-[4px]">{copy.step_body_bn}</div>
      <a
        href={step.deep_link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={reopen}
        className="inline-block mt-[8px] font-anek font-medium text-[14px] leading-[1.75] text-link"
      >
        {copy.reopen_bn}
      </a>
    </div>
  );
}

/** What the door has to say under its controls, other than the field's own error. */
export type DoorNotice =
  | { kind: 'answer'; answer: AuthAnswer }
  | { kind: 'telegram_unavailable' };

export function DoorNoticeLine({
  notice,
  className,
}: {
  notice: DoorNotice | null;
  className?: string | undefined;
}): JSX.Element | null {
  if (notice === null) return null;
  if (notice.kind === 'answer') {
    return <InlineAnswer answer={notice.answer} unexplainedBn={ONBOARDING_COPY.door.not_sent_bn} className={className} />;
  }
  return (
    <div
      role="alert"
      data-copy-status={ONBOARDING_COPY.copy_status}
      className={FIELD_ERROR_LINE + (className ? ' ' + className : '')}
    >
      {ONBOARDING_COPY.door.telegram_unavailable_bn}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════ the hook ══ */

export interface CodeDoor {
  number: string;
  setNumber: (value: string) => void;
  /** `null` while loading; `[]` when the Worker could not be asked — no choice is drawn. */
  options: ChannelOption[] | null;
  chosen: OtpChannel | null;
  choose: (key: OtpChannel) => void;
  telegram: TelegramStep;
  notice: DoorNotice | null;
  /** The field's own error (`১১ সংখ্যার নম্বর দিন`), or `null`. */
  fieldError: string | null;
  primaryLabel: string;
  disabled: boolean;
  busy: boolean;
  submit: () => void;
  reopenTelegram: () => void;
  /** The Turnstile container. Rendered by the screen, `empty:hidden`. */
  challenge: RefObject<HTMLDivElement>;
  /** A full frame standing in for the screen, or `null`. */
  frame: JSX.Element | null;
}

export function useCodeDoor({
  intent,
  next,
  where,
}: {
  intent: CodeIntent;
  next: string | null;
  where: string;
}): CodeDoor {
  const navigate = useNavigate();
  const [number, setRaw] = useState('');
  const [options, setOptions] = useState<ChannelOption[] | null>(null);
  const [chosen, setChosen] = useState<OtpChannel | null>(null);
  const [telegram, setTelegram] = useState<TelegramStep>({ kind: 'idle' });
  const [notice, setNotice] = useState<DoorNotice | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  /** A failure that replaces the screen with a frame. Inline ones go to `notice`. */
  const [shown, setShown] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  /** The double-submit guard. State says "busy" to the eye; this says it to the handler. */
  const inFlight = useRef(false);
  const challenge = useRef<HTMLDivElement>(null);

  /**
   * docs/07 § 3 — what to offer, asked once.
   *
   * NOTHING IS PRESELECTED. A choice the person did not make would be sent as if they had
   * made it, and `channel` overrides what the Worker would otherwise pick — for a
   * returning customer who linked Telegram, their own saved preference. Until a row is
   * tapped the request carries no channel and the Worker chooses, exactly as before the
   * choice existed.
   */
  useEffect(() => {
    const controller = new AbortController();
    fetchChannels(controller.signal).then(
      (rows) => {
        if (!controller.signal.aborted) setOptions(rows);
      },
      () => {
        // No choice can be offered; the Worker picks, as it did before the choice existed.
        if (!controller.signal.aborted) setOptions([]);
      },
    );
    return () => controller.abort();
  }, []);

  const e164 = toE164(number);
  const step = stepFor(telegram, e164);
  const willOpenTelegram = chosen === 'telegram' && step.kind === 'idle';
  const primaryLabel = willOpenTelegram ? ONBOARDING_COPY.door.open_telegram_bn : 'কোড পাঠান';

  const setNumber = (value: string): void => {
    setRaw(value);
    setFieldError(null);
  };

  const choose = (key: OtpChannel): void => {
    setChosen(key);
    setNotice(null);
  };

  const hold = (cause: unknown): void => {
    const answer = answerFor(noteFailure(where, cause));
    if (isInline(answer)) setNotice({ kind: 'answer', answer });
    else setShown(cause);
  };

  const openStep = async (phone_e164: string): Promise<void> => {
    const result = await beginTelegram(phone_e164);
    if (result.outcome === 'opened') {
      setTelegram({
        kind: 'opened',
        phone_e164,
        deep_link: result.deep_link,
        expires_at_ms: Date.now() + result.expires_in_seconds * 1000,
      });
      return;
    }
    if (result.outcome === 'throttled') {
      setNotice({ kind: 'answer', answer: { kind: 'throttled', retry_after_seconds: result.retry_after_seconds } });
      return;
    }
    // No bot configured. Say so, and move the choice to the other channel if offered.
    setNotice({ kind: 'telegram_unavailable' });
    if (options?.some((o) => o.key === 'whatsapp')) setChosen('whatsapp');
  };

  const ask = async (phone_e164: string, channel: OtpChannel | null): Promise<void> => {
    const result = await requestOtp({
      phone_e164,
      challengeToken: takeTurnstileToken(),
      ...(channel === null ? {} : { channel }),
    });
    if (result.outcome === 'sent') {
      const handover: CodeScreenState = {
        phone_e164,
        intent,
        expires_at: result.expires_at,
        ...(channel === null ? {} : { channel }),
        ...(next === null ? {} : { next }),
      };
      navigate('/auth/code', { state: handover });
      return;
    }
    // A NEW number let in with no code: a session exists (`requestOtp` adopted it).
    setShown(new Admitted(result.notice));
  };

  const run = async (work: () => Promise<void>): Promise<void> => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      await work();
    } catch (cause) {
      hold(cause);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const submit = (): void => {
    if (e164 === null || !isPlausibleMobile(e164)) {
      setFieldError(ONBOARDING_COPY.door.number_invalid_bn);
      return;
    }
    if (willOpenTelegram) {
      void run(() => openStep(e164));
      return;
    }
    void run(() => ask(e164, chosen));
  };

  const reopenTelegram = (): void => {
    if (e164 !== null) void run(() => openStep(e164));
  };

  const frame =
    shown === null
      ? null
      : renderAuthFailure(shown, {
          // ইন্টারনেট নেই's one control puts the screen back, with the number still
          // typed — it does not press the button again, and that is deliberate. While the
          // frame was up, `useTurnstile(challenge, frame === null)` had torn the widget
          // down and cleared the token with it, and the container has not remounted in
          // this tick: a resubmit here would call `takeTurnstileToken()`, get null, omit
          // `turnstile_token`, and — wherever the sitekey and the Worker's secret are
          // bound — earn a certain 403 from the guard that runs before anything else. So
          // the body comes back, the widget remounts, the button releases about a second
          // later with a fresh token, and the next press is one that can succeed. The
          // same retry as `code.tsx`'s, for the same reason.
          onRetry: () => setShown(null),
          onBack: () => setShown(null),
          onContinue: () => navigate('/onboarding/profile'),
          onTelegram:
            e164 === null
              ? undefined
              : () => {
                  // An existing identity: no link to make, so the next press asks directly.
                  setShown(null);
                  setChosen('telegram');
                  setTelegram({ kind: 'direct', phone_e164: e164 });
                },
        });

  // The widget lives in the screen's body, so it is mounted only while the body is shown.
  const turnstile = useTurnstile(challenge, frame === null);

  return {
    number,
    setNumber,
    options,
    chosen,
    choose,
    telegram: step,
    notice,
    fieldError,
    primaryLabel,
    disabled: busy || (!willOpenTelegram && !turnstile.ready),
    busy,
    submit,
    reopenTelegram,
    challenge,
    frame,
  };
}
